import {ThemeProvider as MuiThemeProvider} from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import {createTheme, alpha} from '@mui/material';
import {LinkProps} from '@mui/material/Link';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {useUserLanguage} from '@smartmixin/core';
import React, {PropsWithChildren} from 'react';
import {useSelector} from 'react-redux';
import {Link as RouterLink, LinkProps as RouterLinkProps} from 'react-router';
import {selectIsDarkTheme} from '../store/prefs/selectors';
import {
  weatherColors,
  statusColors,
  brandColors,
  borderRadius,
  shadows,
  shadowsDark,
  typography,
  navigation,
  accentColors,
} from './tokens';

// Augment MUI theme to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    hot: Palette['primary'];
    cold: Palette['primary'];
    rain: Palette['primary'];
    snow: Palette['primary'];
    weather: {
      hot: string;
      warm: string;
      mild: string;
      cool: string;
      cold: string;
      rain: string;
      sun: string;
      wind: string;
      snow: string;
      storm: string;
      fog: string;
      cloud: string;
    };
    status: {
      online: string;
      stale: string;
      offline: string;
      syncing: string;
    };
    /** The forecast picker's state strip. See `accentColors` in tokens.ts. */
    accent: {
      overriddenFill: string;
      overriddenOn: string;
      followingFill: string;
      followingOn: string;
    };
  }
  interface PaletteOptions {
    hot?: PaletteOptions['primary'];
    cold?: PaletteOptions['primary'];
    rain?: PaletteOptions['primary'];
    snow?: PaletteOptions['primary'];
    weather?: Palette['weather'];
    status?: Palette['status'];
    accent?: Palette['accent'];
  }
}

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & {href: RouterLinkProps['to']}
>((props, ref) => {
  const {href, ...other} = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});

const ThemeProvider = ({children}: PropsWithChildren) => {
  const isDarkTheme = useSelector(selectIsDarkTheme);
  const language = useUserLanguage();

  const theme = React.useMemo(() => {
    const currentShadows = isDarkTheme ? shadowsDark : shadows;

    return createTheme({
      // Typography
      typography: {
        fontFamily: typography.fontFamily,
        button: {
          textTransform: 'none',
          fontWeight: typography.weights.medium,
        },
        h1: {
          fontWeight: typography.weights.bold,
          fontSize: typography.sizes['4xl'],
          lineHeight: typography.lineHeights.tight,
        },
        h2: {
          fontWeight: typography.weights.bold,
          fontSize: typography.sizes['3xl'],
          lineHeight: typography.lineHeights.tight,
        },
        h3: {
          fontWeight: typography.weights.semibold,
          fontSize: typography.sizes['2xl'],
          lineHeight: typography.lineHeights.tight,
        },
        h4: {
          fontWeight: typography.weights.semibold,
          fontSize: typography.sizes.xl,
          lineHeight: typography.lineHeights.normal,
        },
        h5: {
          fontWeight: typography.weights.medium,
          fontSize: typography.sizes.lg,
          lineHeight: typography.lineHeights.normal,
        },
        h6: {
          fontWeight: typography.weights.medium,
          fontSize: typography.sizes.md,
          lineHeight: typography.lineHeights.normal,
        },
        body1: {
          fontSize: typography.sizes.md,
          lineHeight: typography.lineHeights.normal,
        },
        body2: {
          fontSize: typography.sizes.sm,
          lineHeight: typography.lineHeights.normal,
        },
        caption: {
          fontSize: typography.sizes.xs,
          lineHeight: typography.lineHeights.normal,
        },
      },

      // Palette
      palette: {
        mode: isDarkTheme ? 'dark' : 'light',
        primary: {main: brandColors.primary},
        secondary: {main: brandColors.secondary},
        hot: {main: weatherColors.hot},
        cold: {main: weatherColors.cold},
        rain: {main: weatherColors.rain},
        snow: {main: weatherColors.snow},
        weather: weatherColors,
        status: statusColors,
        accent: isDarkTheme ? accentColors.dark : accentColors.light,
        background: {
          paper: isDarkTheme ? 'rgb(18, 20, 22)' : 'rgb(255, 255, 255)',
          default: isDarkTheme ? 'rgb(8, 10, 12)' : 'rgb(248, 250, 252)',
        },
        divider: isDarkTheme
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.08)',
      },

      // Shape
      shape: {
        borderRadius: borderRadius.md,
      },

      // Component overrides
      components: {
        // Link behavior for router
        MuiLink: {
          defaultProps: {
            component: LinkBehavior,
          } as LinkProps,
        },
        MuiButtonBase: {
          defaultProps: {
            LinkComponent: LinkBehavior,
          },
        },

        // Card styling
        MuiCard: {
          defaultProps: {
            variant: 'outlined',
          },
          styleOverrides: {
            root: {
              borderRadius: borderRadius.lg,
              boxShadow: 'none',
              borderColor: isDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
              transition: 'border-color 200ms, box-shadow 200ms',
              '&:hover': {
                borderColor: isDarkTheme
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(0, 0, 0, 0.15)',
              },
            },
          },
        },

        // Paper styling.
        // Intentionally do NOT default Paper to `outlined`: that gives every
        // Paper-based surface (AppBar, the permanent Drawers, menus, dialogs,
        // popovers) a 1px border and a 12px (borderRadius.lg) corner radius,
        // rounding full-bleed surfaces that must stay square. Paper keeps MUI's
        // default `elevation` variant (matches prod); the `outlined` overrides
        // below still apply to any Paper explicitly set to that variant. Cards
        // have their own `outlined` default (MuiCard) and are unaffected.
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            outlined: {
              borderRadius: borderRadius.lg,
              borderColor: isDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
            },
          },
        },

        // Button styling
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.md,
              padding: '8px 16px',
              fontWeight: typography.weights.medium,
            },
            contained: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: currentShadows.card,
              },
            },
            outlined: {
              borderColor: isDarkTheme
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)',
            },
          },
        },

        // TextField styling
        MuiTextField: {
          defaultProps: {
            variant: 'outlined',
            size: 'small',
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.md,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
              },
            },
          },
        },

        // Chip styling
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.sm,
              fontWeight: typography.weights.medium,
            },
          },
        },

        // Skeleton styling with wave animation
        MuiSkeleton: {
          defaultProps: {
            animation: 'wave',
          },
          styleOverrides: {
            root: {
              borderRadius: borderRadius.sm,
              backgroundColor: isDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
            },
          },
        },

        // Tab styling
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: typography.weights.medium,
              minHeight: 48,
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              height: 3,
              borderRadius: 3,
            },
          },
        },

        // List item styling
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.md,
              margin: '2px 8px',
              padding: '8px 12px',
              '&.Mui-selected': {
                backgroundColor: alpha(
                  brandColors.primary,
                  isDarkTheme ? 0.15 : 0.1,
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    brandColors.primary,
                    isDarkTheme ? 0.2 : 0.15,
                  ),
                },
              },
            },
          },
        },

        // Drawer styling
        MuiDrawer: {
          styleOverrides: {
            paper: {
              borderRight: `1px solid ${
                isDarkTheme
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
            },
          },
        },

        // AppBar styling
        MuiAppBar: {
          defaultProps: {
            elevation: 0,
          },
          styleOverrides: {
            root: {
              backgroundColor: isDarkTheme
                ? 'rgb(18, 20, 22)'
                : 'rgb(255, 255, 255)',
              borderBottom: `1px solid ${
                isDarkTheme
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
            },
          },
        },

        // Tooltip styling
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: borderRadius.sm,
              fontSize: typography.sizes.xs,
              fontWeight: typography.weights.medium,
            },
          },
        },

        // Dialog styling
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: borderRadius.xl,
              boxShadow: currentShadows.modal,
            },
          },
        },

        // Alert styling
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.md,
            },
          },
        },

        // Toolbar
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: navigation.appBarHeight,
              '@media (max-width: 600px)': {
                minHeight: navigation.appBarHeightMobile,
              },
            },
          },
        },

        // IconButton
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: borderRadius.md,
            },
          },
        },

        // BottomNavigation
        MuiBottomNavigation: {
          styleOverrides: {
            root: {
              height: navigation.bottomNavHeight,
              borderTop: `1px solid ${
                isDarkTheme
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
            },
          },
        },
        MuiBottomNavigationAction: {
          styleOverrides: {
            root: {
              minWidth: 64,
              padding: '6px 12px',
              '&.Mui-selected': {
                color: brandColors.primary,
              },
            },
            label: {
              fontSize: typography.sizes.xs,
              '&.Mui-selected': {
                fontSize: typography.sizes.xs,
              },
            },
          },
        },
      },
    });
  }, [isDarkTheme]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </LocalizationProvider>
  );
};

export default ThemeProvider;
